/* eslint-disable react-refresh/only-export-components */
import React from 'react'

import { ServiceQuestion } from '@/features/service-questions/api/get-question'
import { useNonReactiveCallback } from '@/hooks/use-non-reactive-callbacks'

export type ModalVariant = 'auto' | 'dialog' | 'drawer' | 'side-drawer'
export type ModalPlacement = 'bottom' | 'top' | 'left' | 'right'

export type ModalSlots = {
  Content: React.ComponentType<React.HTMLAttributes<HTMLDivElement>>
  Header: React.ComponentType<React.HTMLAttributes<HTMLDivElement>>
  Body: React.ComponentType<React.HTMLAttributes<HTMLDivElement>>
  Footer: React.ComponentType<React.HTMLAttributes<HTMLDivElement>>
  Title: React.ComponentType<React.HTMLAttributes<HTMLHeadingElement>>
  Description: React.ComponentType<React.HTMLAttributes<HTMLParagraphElement>>
  Close: React.ComponentType<React.ComponentPropsWithoutRef<'button'>>
}

export interface ModalRenderContext {
  close: () => void
  closeAll: () => void
  resolve: (value?: unknown) => void
}

export type ModalRenderer = (slots: ModalSlots, ctx: ModalRenderContext) => React.ReactNode

interface Base {
  name: string
  title?: string
  variant?: ModalVariant
  dismissible?: boolean
  placement?: ModalPlacement
  render?: ModalRenderer
  __resolve?: (value?: unknown) => void
}

export interface CreateServiceModal extends Base {
  name: 'create-service'
  withTemplates?: boolean
}

export interface CreatePatientModal extends Base {
  name: 'create-patient'
  pid: string
}

export interface EditPatientModal extends Base {
  name: 'edit-patient'
  pid: string
}

export interface QuestionViewModal extends Base {
  name: 'question-view'
  qid: string
}

export interface EditQuestionModal extends Base {
  name: 'edit-question'
  qid: string
}

export interface ShareInviteUrlModal extends Base {
  name: 'share-invite-code'
  url: string
}

export interface CreateQuestionModal extends Base {
  name: 'create-question'
  tid: string
  question?: ServiceQuestion
}

export interface ConfirmDialogModal extends Base {
  name: 'confirm'
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  intent?: 'default' | 'danger'
  icon?: React.ReactNode
  onConfirm?: () => void | Promise<void>
  onCancel?: () => void | Promise<void>
  resolve?: (result: 'confirm' | 'cancel') => void
}

export type Modal =
  // Service
  | CreateServiceModal
  // Patients
  | CreatePatientModal
  // Patient
  | EditPatientModal
  // Question
  | QuestionViewModal
  | EditQuestionModal
  // Questions
  | CreateQuestionModal
  // Invite
  | ShareInviteUrlModal
  // Generic confirm dialog
  | ConfirmDialogModal

export type ModalRenderArgs<M extends Modal = Modal> = {
  modal: M
  slots: ModalSlots
  ctx: ModalRenderContext
}

export type ModalRegistryMap = {
  [K in Modal['name']]: (args: ModalRenderArgs<Extract<Modal, { name: K }>>) => React.ReactNode
}

const ModalContext = React.createContext<{
  isModalActive: boolean
  activeModals: Modal[]
}>({
  isModalActive: false,
  activeModals: [],
})

const ModalControlContext = React.createContext<{
  openModal: (modal: Modal) => void
  openModalPromise: <R = unknown>(modal: Modal) => Promise<R>
  closeModal: () => boolean
  closeAllModals: () => boolean
  resolveModal: (value?: unknown) => boolean
}>({
  openModal: () => {},
  openModalPromise: async () => {
    return undefined as unknown as never
  },
  closeModal: () => false,
  closeAllModals: () => false,
  resolveModal: () => false,
})

export function Provider({ children }: React.PropsWithChildren<object>) {
  const [activeModals, setActiveModals] = React.useState<Modal[]>([])

  const settleModal = React.useCallback((modal: Modal | undefined, value?: unknown) => {
    if (modal && typeof modal.__resolve === 'function') {
      modal.__resolve(value)
      modal.__resolve = undefined
    }
  }, [])

  const openModal = useNonReactiveCallback((modal: Modal) => {
    setActiveModals((modals) => [...modals, modal])
  })

  const openModalPromise = useNonReactiveCallback(<R = unknown,>(modal: Modal) => {
    return new Promise<R>((resolve) => {
      const resolver = (value?: unknown) => {
        resolve(value as R)
      }
      setActiveModals((modals) => [...modals, { ...modal, __resolve: resolver }])
    })
  })

  const closeModal = useNonReactiveCallback(() => {
    const wasActive = activeModals.length > 0
    setActiveModals((modals) => {
      const next = modals.slice(0, -1)
      const top = modals[modals.length - 1]
      settleModal(top)
      return next
    })
    return wasActive
  })

  const closeAllModals = useNonReactiveCallback(() => {
    const wasActive = activeModals.length > 0
    setActiveModals((modals) => {
      modals.forEach((modal) => settleModal(modal))
      return []
    })
    return wasActive
  })

  const resolveModal = useNonReactiveCallback((value?: unknown) => {
    const wasActive = activeModals.length > 0
    setActiveModals((modals) => {
      const next = modals.slice(0, -1)
      const top = modals[modals.length - 1]
      settleModal(top, value)
      return next
    })
    return wasActive
  })

  const state = React.useMemo(
    () => ({
      isModalActive: activeModals.length > 0,
      activeModals,
    }),
    [activeModals],
  )

  const methods = React.useMemo(
    () => ({
      openModal,
      openModalPromise,
      closeModal,
      closeAllModals,
      resolveModal,
    }),
    [openModal, openModalPromise, closeModal, closeAllModals, resolveModal],
  )

  return (
    <ModalContext.Provider value={state}>
      <ModalControlContext.Provider value={methods}>{children}</ModalControlContext.Provider>
    </ModalContext.Provider>
  )
}

export function useModals() {
  return React.useContext(ModalContext)
}

export function useModalControls() {
  return React.useContext(ModalControlContext)
}
